import { setMediaMetadata } from '@/audio/mediaSession'
import { engine } from '@/audio/engine'
import { usePlayer } from '@/audio/store'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode, useEffect, useLayoutEffect, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, useLocation } from 'react-router'

import { PlayerProvider } from '@/audio/PlayerProvider'
import { applyTheme, getTheme } from '@/lib/theme'
import '@/styles/index.css'

import { App } from './App'

// index.html already set the class before first paint; this re-asserts it and
// brings the module's media-query listener online for the rest of the session.
applyTheme(getTheme())

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // A music library does not change behind the user's back often enough to
      // justify refetching every time they tab back in.
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

const container = document.getElementById('root')
if (!container) {
  throw new Error('Missing #root element — index.html and main.tsx are out of sync.')
}

function PersonalPlayerBoundary({ children }: { children: ReactNode }) {
  const guest = useLocation().pathname.startsWith('/share/')
  useLayoutEffect(() => {
    if (guest) { engine.pause(); usePlayer.getState()._sync({ isPlaying: false }) }
  }, [guest])
  useEffect(() => {
    if (!guest) setMediaMetadata(usePlayer.getState().current() ?? null)
  }, [guest])
  return guest ? children : <PlayerProvider>{children}</PlayerProvider>
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Personal wiring survives normal navigation; guest routes suspend it
          while retaining the personal engine and queue for return. */}
      <BrowserRouter>
        <PersonalPlayerBoundary><App /></PersonalPlayerBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
